"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";

import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import SafeHtml from "@/components/ui/SafeHtml";
import { useLayoutData } from "@/components/layout/LayoutDataProvider";

import {
  itemMetaLines,
  toInt,
  resolveLocaleFromLang,
  type CartItem,
  type Coupon,
  type PaymentMethod,
} from "../../lib/checkoutHelpers";

type WooMoneyObject = { formatted?: string; value?: string | number };
type WooMoneyLike = string | number | WooMoneyObject | null | undefined;

type Props = {
  lang: string;
  items: CartItem[];
  subtotal: string | null;
  total: string | null;
  discount: string | null;
  meEmail: string | null;
  placing: boolean;
  onPlaceOrder: () => void;
  appliedCoupons: Coupon[];
  onApplyPromo: (code: string) => Promise<void>;
  onRemovePromo: (code: string) => Promise<void>;
  selectedPaymentMethod: PaymentMethod;
  onChangePaymentMethod: (method: PaymentMethod) => void;
  mbwayPhone: string;
  onChangeMbwayPhone: (phone: string) => void;
  mbwayPhoneError: string | null;
  onClearMbwayPhoneError: () => void;
  isFreeOrder?: boolean;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
function hasCurrencyFormattedString(value: string) {
  return /[€$£¥]|eur|usd|gbp/i.test(value);
}
function getMoneyPrimitive(raw: WooMoneyLike): string | number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string" || typeof raw === "number") return raw;
  if (typeof raw.formatted === "string" && raw.formatted.trim()) return raw.formatted.trim();
  if (typeof raw.value === "string" || typeof raw.value === "number") return raw.value;
  return null;
}
function getCurrencyMeta(source: unknown): { currencyCode: string | null; minorUnit: number | null } {
  if (!isRecord(source)) return { currencyCode: null, minorUnit: null };
  const currencyCodeRaw = source["currency_code"];
  const minorUnitRaw = source["currency_minor_unit"];
  return {
    currencyCode: typeof currencyCodeRaw === "string" && currencyCodeRaw.trim() ? currencyCodeRaw.trim() : null,
    minorUnit: typeof minorUnitRaw === "number" && Number.isFinite(minorUnitRaw) ? minorUnitRaw : null,
  };
}

function formatWooMinorMoney(value: string | number, currencyCode: string, minorUnit: number, locale: string) {
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) return "—";
  return new Intl.NumberFormat(locale, { style: "currency", currency: currencyCode, minimumFractionDigits: minorUnit, maximumFractionDigits: minorUnit }).format(numeric / Math.pow(10, minorUnit));
}

function formatItemLineTotal(item: CartItem, locale: string) {
  // Prefer line_subtotal (pre-discount) so the discount only appears once,
  // in its own row below the subtotal — not double-counted here.
  const rawMoney = (item.totals?.line_subtotal ?? item.totals?.line_total ?? item.totals?.total ?? null) as WooMoneyLike;
  const primitive = getMoneyPrimitive(rawMoney);
  if (primitive === null) return "—";
  if (typeof primitive === "string" && hasCurrencyFormattedString(primitive)) return primitive;
  const { currencyCode } = getCurrencyMeta(item.totals);
  const { minorUnit } = { ...getCurrencyMeta(item.totals), ...getCurrencyMeta(item.prices) };
  if (!currencyCode || minorUnit === null) return typeof primitive === "string" || typeof primitive === "number" ? String(primitive) : "—";
  return formatWooMinorMoney(primitive, currencyCode, minorUnit, locale);
}

type PaymentOptionProps = {
  value: PaymentMethod;
  label: string;
  description: string;
  selected: boolean;
  onChange: (m: PaymentMethod) => void;
  children?: React.ReactNode;
};

function PaymentOption({ value, label, description, selected, onChange, children }: PaymentOptionProps) {
  return (
    <label className="block cursor-pointer rounded-xl border p-4">
      <div className="flex items-start gap-3">
        <input type="radio" name="payment-method" value={value} checked={selected} onChange={() => onChange(value)} className="mt-1" />
        <div className="min-w-0 w-full">
          <div className="font-medium">{label}</div>
          <div className="mt-1 text-sm opacity-80">{description}</div>
          {children}
        </div>
      </div>
    </label>
  );
}

export default function CheckoutSidebar({
  lang, items, subtotal, total, discount, meEmail, placing, onPlaceOrder,
  appliedCoupons, onApplyPromo, onRemovePromo,
  selectedPaymentMethod, onChangePaymentMethod,
  mbwayPhone, onChangeMbwayPhone, mbwayPhoneError, onClearMbwayPhoneError,
  isFreeOrder = false,
}: Props) {
  const locale = useMemo(() => resolveLocaleFromLang(lang), [lang]);

  const { global_text } = useLayoutData();
  const CHECKOUT: Record<string, string> =
    (global_text as { cart?: { checkout?: Record<string, string> } } | undefined)
      ?.cart?.checkout ?? {};
  const t = (k: string, fallback = "") => CHECKOUT[k] || fallback;

  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoMsgTone, setPromoMsgTone] = useState<"ok" | "err">("ok");
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const promoRef = useRef<HTMLInputElement | null>(null);
  const mbwayPhoneRef = useRef<HTMLInputElement | null>(null);
  const normalizedCoupons = useMemo(() => Array.isArray(appliedCoupons) ? appliedCoupons : [], [appliedCoupons]);

  useEffect(() => { if (promoCodeError) promoRef.current?.focus(); }, [promoCodeError]);
  useEffect(() => {
    if (!isFreeOrder && selectedPaymentMethod === "mbway_ifthen_for_woocommerce" && mbwayPhoneError) mbwayPhoneRef.current?.focus();
  }, [isFreeOrder, selectedPaymentMethod, mbwayPhoneError]);

  function resetPromoErrors() { setPromoCodeError(null); setPromoMsg(null); setPromoMsgTone("ok"); }

  async function apply() {
    resetPromoErrors();
    const code = promoCode.trim();
    if (!code) { setPromoCodeError(t("promo_required_error", "Enter a promo code.")); return; }
    setPromoBusy(true);
    try {
      await onApplyPromo(code);
      setPromoMsgTone("ok"); setPromoMsg(t("promo_applied_success", "Promo code applied.")); setPromoCode("");
    } catch (error: unknown) {
      setPromoMsgTone("err"); setPromoMsg(error instanceof Error ? error.message : t("promo_apply_error", "Could not apply promo code."));
    } finally { setPromoBusy(false); }
  }

  async function remove(code: string) {
    resetPromoErrors();
    const trimmed = code.trim();
    if (!trimmed) return;
    setPromoBusy(true);
    try {
      await onRemovePromo(trimmed);
      setPromoMsgTone("ok"); setPromoMsg(t("promo_removed_success", "Promo code removed."));
    } catch (error: unknown) {
      setPromoMsgTone("err"); setPromoMsg(error instanceof Error ? error.message : t("promo_remove_error", "Could not remove promo code."));
    } finally { setPromoBusy(false); }
  }

  return (
    <div className="lg:col-span-4">
      <div>
        {/* Order summary */}
        <div className="border-b overflow-hidden">
          <div className="px-6 py-4 border-t-4 border-fuji-green bg-fuji-grey font-bold">{t("sidebar_order_heading", "Your order")}</div>
          <div className="px-6 py-4 border-b">
            <div className="grid grid-cols-12 text-sm font-bold">
              <div className="col-span-8">{t("sidebar_product", "Product")}</div>
              <div className="col-span-4 text-right">{t("sidebar_subtotal_col", "Subtotal")}</div>
            </div>
          </div>
          {items.length > 0 ? (
            <div className="px-6 py-4 border-b">
              <div className="flex flex-col gap-4">
                {items.map((item, index) => {
                  const key = String(item.key ?? item.id ?? index);
                  const name = item.name || t("sidebar_fallback_item_name", "Item");
                  const qty = toInt(item.quantity, 1);
                  const meta = itemMetaLines(item);
                  const lineTotal = formatItemLineTotal(item, locale);
                  return (
                    <div key={key} className="grid grid-cols-12 gap-3 text-sm">
                      <div className="col-span-8 min-w-0">
                        <div className="font-medium break-words overflow-wrap-anywhere">
                          {item.permalink ? (
                            <Link href={item.permalink} className="transition-opacity hover:opacity-70"><SafeHtml content={name} /></Link>
                          ) : <SafeHtml content={name} />}
                        </div>
                        <div className="opacity-80">× {qty}</div>
                        {meta.length > 0 ? <div className="mt-1 text-body-3 opacity-80">{meta.map((line) => <div key={line}>{line}</div>)}</div> : null}
                      </div>
                      <div className="col-span-4 text-right font-medium">{lineTotal}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="px-6 py-5 border-b">
            <div className="flex justify-between"><span className="opacity-80 font-bold">{t("sidebar_subtotal_label", "Subtotal")}</span><span className="font-medium">{subtotal ?? "—"}</span></div>
          </div>

          {/* Discount row(s) — one per applied coupon, with total discount amount on the first */}
          {normalizedCoupons.length > 0 && discount ? (
            <div className="px-6 py-5 border-b">
              <div className="flex flex-col gap-2">
                {normalizedCoupons.map((coupon, index) => {
                  const code = coupon.code ?? coupon.coupon ?? coupon.coupon_code ?? coupon.name ?? `COUPON-${index}`;
                  return (
                    <div key={String(code)} className="flex justify-between">
                      <span className="font-medium">
                        {t("sidebar_discount_label")}: <span className="opacity-80">{String(code)}</span>
                      </span>
                      {index === 0 ? <span className="font-medium">−{discount}</span> : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="px-6 py-5 border-b">
            <div className="flex justify-between font-bold text-base"><span>{t("sidebar_total_label", "Total")}</span><span>{total ?? "—"}</span></div>
          </div>
        </div>

        {/* Promo code */}
        <div className="mt-6 rounded-xl border p-5">
          <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setPromoOpen((v) => !v)} aria-expanded={promoOpen}>
            <span className="font-bold">{t("promo_heading", "Promo code")}</span>
            <span className="text-sm opacity-80">{promoOpen ? t("promo_hide", "Hide") : t("promo_add", "Add")}</span>
          </button>
          {promoOpen ? (
            <div className="mt-4">
              {normalizedCoupons.length > 0 ? (
                <div className="text-sm">
                  <div className="mb-2 opacity-80">{t("promo_applied_label", "Applied:")}</div>
                  <div className="flex flex-col gap-2">
                    {normalizedCoupons.map((coupon, index) => {
                      const code = coupon.code ?? coupon.coupon ?? coupon.coupon_code ?? coupon.name ?? `COUPON-${index}`;
                      return (
                        <div key={String(code)} className="flex items-center justify-between gap-3">
                          <span className="font-medium">{String(code)}</span>
                          <button type="button" className={`text-sm underline ${promoBusy ? "cursor-not-allowed opacity-60" : ""}`} disabled={promoBusy} onClick={() => remove(String(code))}>{t("promo_remove", "Remove")}</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div className="mt-3 flex items-end gap-3">
                <div className="min-w-0 flex-1">
                  <Input id="promo-code" ref={promoRef} label={t("promo_code_label", "Code")} value={promoCode}
                    onChange={(e) => { setPromoCodeError(null); setPromoMsg(null); setPromoCode(e.target.value); }}
                    aria-invalid={promoCodeError ? true : undefined} aria-describedby={promoCodeError ? "promo-code-error" : undefined} invalid={!!promoCodeError} />
                  {promoCodeError ? <small id="promo-code-error" role="alert" className="mt-1 block text-body-3 text-fuji-red">{promoCodeError}</small> : null}
                </div>
                <Button type="button" className="text-body" center bgStyle="bg-fuji-grey hover:bg-fuji-grey/80 border-fuji-grey" disabled={promoBusy} loading={promoBusy} onClick={apply}>{t("promo_apply_button", "Apply")}</Button>
              </div>
              {promoMsg ? (
                promoMsgTone === "err" ? (
                  <div className="mt-4 overflow-hidden rounded-2xl border">
                    <div className="border-t-4 border-fuji-red bg-fuji-grey px-5 py-4">
                      <div className="font-bold">{t("promo_apply_error_title", "Could not apply promo code")}</div>
                      <div className="mt-1 text-sm opacity-80"><SafeHtml content={promoMsg} /></div>
                    </div>
                  </div>
                ) : <div className="mt-3 text-sm opacity-80"><SafeHtml content={promoMsg} /></div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Payment method */}
        {isFreeOrder ? (
          <div className="mt-4 rounded-xl border p-5">
            <div className="font-bold">{t("payment_free_title", "No charge")}</div>
            <div className="mt-1 text-sm opacity-80">
              {t("payment_free_description", "This order has no charge. Click Place order to confirm your booking.")}
            </div>
          </div>
        ) : (
          <div className="mt-4 rounded-xl border p-5">
            <div className="font-bold">{t("payment_heading", "Payment method")}</div>
            <div className="mt-3 flex flex-col gap-3">
              <PaymentOption
                value="mbway_ifthen_for_woocommerce"
                label={t("payment_mbway_label", "MB WAY")}
                description={t("payment_mbway_description", "Approve the payment in your MB WAY app on your phone.")}
                selected={selectedPaymentMethod === "mbway_ifthen_for_woocommerce"}
                onChange={onChangePaymentMethod}
              >
                {selectedPaymentMethod === "mbway_ifthen_for_woocommerce" ? (
                  <div className="mt-4">
                    <Input id="mbway-phone" ref={mbwayPhoneRef} label={t("payment_mbway_phone_label", "MB WAY mobile number")} type="tel"
                      value={mbwayPhone}
                      onChange={(e) => { onClearMbwayPhoneError(); onChangeMbwayPhone(e.target.value.replace(/\D/g, "")); }}
                      placeholder={t("payment_mbway_phone_placeholder", "9xxxxxxxx")} autoComplete="tel" inputMode="numeric"
                      aria-invalid={!!mbwayPhoneError || undefined} aria-describedby={mbwayPhoneError ? "mbway-phone-error" : undefined}
                      invalid={!!mbwayPhoneError} required />
                    {mbwayPhoneError ? <small id="mbway-phone-error" role="alert" className="mt-1 block text-body-3 text-fuji-red">{mbwayPhoneError}</small> : null}
                  </div>
                ) : null}
              </PaymentOption>

              <PaymentOption
                value="multibanco_ifthen_for_woocommerce"
                label={t("payment_multibanco_label", "Multibanco")}
                description={t("payment_multibanco_description", "Pay at any Multibanco ATM or via home banking. Entity and reference shown after checkout. Portuguese banks only.")}
                selected={selectedPaymentMethod === "multibanco_ifthen_for_woocommerce"}
                onChange={onChangePaymentMethod}
              />

              <PaymentOption
                value="creditcard_ifthen_for_woocommerce"
                label={t("payment_creditcard_label", "Credit or debit card")}
                description={t("payment_creditcard_description", "Pay securely by card. You'll be redirected to complete payment after placing the order.")}
                selected={selectedPaymentMethod === "creditcard_ifthen_for_woocommerce"}
                onChange={onChangePaymentMethod}
              />
            </div>
          </div>
        )}

        {/* Place order */}
        <Button type="button" className="w-full text-body mt-4"
          bgStyle="bg-fuji-green hover:bg-fuji-green/80 border-fuji-green hover:border-fuji-green/80"
          center disabled={placing || items.length === 0} loading={placing}
          onClick={() => { setPromoMsg(null); onPlaceOrder(); }}>
          {placing ? t("place_order_button_loading", "Placing order…") : t("place_order_button", "Place order")}
        </Button>

        {!meEmail ? <div className="mt-3 text-sm opacity-80">{t("account_required_short", "Account required.")}</div> : null}
      </div>
    </div>
  );
}